import React, { useState, useEffect, useCallback, useMemo } from "react";

/** Utils */
import { withRouter, Redirect } from "react-router";
import {
  getCompanyCards,
  buyAttendees,
  getAttendeeService,
  getEvent,
  getEventCartItem,
  getBillingCouponInfoByPromoCode,
  getCompanyBillingInfo,
  calculateTaxByCurrency,
  checkoutCart,
} from "../api/calls";
import queryString from "query-string";

import { ReactComponent as CheckSvg } from "../assets/svg/check.svg";
import { ReactComponent as CrossSvg } from "../assets/svg/close.svg";

/** Assets */
import VisaCard from "../assets/card-providers/dark/visa.png";

const EventCart = (props) => {
  const { match, stripe, elements, location } = props;

  const [pricingInfo, setPricingInfo] = useState(null);
  const [event, setEvent] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [tax, setTax] = useState({});

  const totalPrice = useMemo(
    () =>
      cartItems.reduce(
        (acc, curr) => acc + curr.quantity * curr.pricing.price,
        0
      ),
    [cartItems]
  );
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeId, setPromoCodeId] = useState("");
  const [promoCodeAmount, setPromoCodeAmount] = useState(0);
  const [isPromoCode, setIsPromoCode] = useState(false);
  const [promoDetails, setPromoDetails] = useState(null);
  const totalTax = useMemo(() => {
    const appliedDiscount = Math.min(promoCodeAmount, totalPrice);
    if (!Array.isArray(cartItems) || cartItems.length === 0) return 0;

    // determine eligible subtotal for this promo (if promoDetails restricts to certain items)
    const eligibleSubtotal = (() => {
      if (!promoDetails) return totalPrice;
      if (promoDetails.appliesToAll) return totalPrice;
      const ids = promoDetails.contentIds || [];
      if (!Array.isArray(ids) || ids.length === 0) return totalPrice;
      return cartItems.reduce((s, it) => {
        const itemSubtotal = it.quantity * it.pricing.price;
        const candidates = [
          it.item_detail?.id,
          it.item_detail?.databaseId,
          it.contentId,
          it.content?.id,
          it.productId,
          it.product?.id,
        ];
        const match = candidates.some((c) => c && ids.indexOf(String(c)) !== -1);
        return s + (match ? itemSubtotal : 0);
      }, 0);
    })();

    return cartItems.reduce((acc, curr) => {
      const itemSubtotal = curr.quantity * curr.pricing.price;
      const taxPercent = isNaN(curr.pricing.tax) ? 0 : curr.pricing.tax;

      // apply discount only to eligible items, proportionally across eligible subtotal
      let itemDiscount = 0;
      const isEligible = (() => {
        if (!promoDetails) return true;
        if (promoDetails.appliesToAll) return true;
        const ids = promoDetails.contentIds || [];
        if (!Array.isArray(ids) || ids.length === 0) return true;
        const candidates = [
          curr.item_detail?.id,
          curr.item_detail?.databaseId,
          curr.contentId,
          curr.content?.id,
          curr.productId,
          curr.product?.id,
        ];
        return candidates.some((c) => c && ids.indexOf(String(c)) !== -1);
      })();

      if (isEligible && eligibleSubtotal > 0) {
        itemDiscount = appliedDiscount * (itemSubtotal / eligibleSubtotal);
      }

      const taxableAmount = Math.max(itemSubtotal - itemDiscount, 0);
      return acc + (taxableAmount * taxPercent) / 100;
    }, 0);
  }, [cartItems, promoCodeAmount, totalPrice, promoDetails]);
  const currency = useMemo(
    () => (cartItems.length > 0 ? cartItems[0].pricing?.currency : ""),
    [cartItems]
  );

  const allowedParentOrigin = useMemo(() => {
    const parsed = queryString.parse(location.search);
    const callbackUrl = parsed?.callback;
    if (typeof callbackUrl === "string") {
      try {
        return new URL(callbackUrl).origin;
      } catch (error) {
        console.warn("Invalid callback URL provided to event cart", error);
      }
    }
    return null;
  }, [location.search]);

  

  const [selectedCard, setSelectedCard] = useState(null);
  const [companyCards, setCompanyCards] = useState([]);
  const [billingEmail, setBillingEmail] = useState(null);
  const [billingPhone, setBillingPhone] = useState(null);

  const [confirmStep, setConfirmStep] = useState(0);
  const [intentClientSecret, setIntentClientSecret] = useState(null);
  const [pendingCardId, setPendingCardId] = useState(null);
  const [pendingPromo, setPendingPromo] = useState(null);
  const [autoApplyPromo, setAutoApplyPromo] = useState(null);

  const getTax = useCallback(async () => {
    if (!pricingInfo) return;
    const taxInfo = await calculateTaxByCurrency(
      pricingInfo.price / 100 - promoCodeAmount,
      pricingInfo?.currency
    );
    setTax({
      taxPayable: taxInfo?.data?.calculateTaxByCurrency?.payable,
      taxRate: taxInfo?.data?.calculateTaxByCurrency?.rate?.rate,
      taxType: taxInfo?.data?.calculateTaxByCurrency?.rate?.type,
    });
  }, [pricingInfo, promoCodeAmount]);

  const getEventProps = useCallback(async () => {
    try {
      if (!match?.params?.eventId) return;
      const { data } = await getEvent(match?.params?.eventId);
      const { data: dataCart } = await getEventCartItem(match?.params?.eventId);

      setEvent(data.getEvent);
      setCartItems(
        (dataCart.getEventCartItem || []).filter(
          (item) =>
            item.status === "PENDING" || item.status === "PAYMENT_FAILED"
        )
      );
    } catch (error) {
      console.log(error);
    }
  }, [match?.params?.eventId]);

  const getCards = useCallback(async () => {
    try {
      const companyId = event?.organiser?.company?.id;
      const {
        data: { getPaymentCards: result },
      } = await getCompanyCards(companyId);
      console.log("Company: ", event?.organiser?.company);
      setCompanyCards(result || []);
    } catch (error) {
      console.log(error);
    }
  }, [event?.organiser?.company?.id]);

  const getService = useCallback(async () => {
    try {
      if (!event) return;
      const { data } = await getAttendeeService(event?.id);

      setPricingInfo({
        serviceId: data.getAttendeeService.service.id,
        price: data.getAttendeeService.price,
        quantity: data.getAttendeeService.amount,
        serviceName: data.getAttendeeService.service.name,
        currency: data.getAttendeeService?.currency,
        companyName: data.getAttendeeService.company.name,
        taxName: data.getAttendeeService.company.taxName,
        taxAmount: data.getAttendeeService.company.taxAmount,
      });
    } catch (error) {
      console.log(error);
    }
  }, [event]);

  const checkoutItem = useCallback(async () => {
    try {
      let { data } = await checkoutCart(
        event?.id,
        selectedCard.stripeId,
        promoCodeId
      );

      if (data && data.checkoutEventCartItemWithCard && !intentClientSecret) {
        setIntentClientSecret(data.checkoutEventCartItemWithCard.client_secret);

        setConfirmStep(2);
      }
    } catch (error) {
      console.log(error);
    }
  }, [event, pricingInfo, promoCodeId, selectedCard]);

  const formatNumber = (value) => {
    if (value) {
      let numParts = value.toString().split(".");
      numParts[0] = numParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return numParts.join(".");
    }
    return value;
  };

  const renderCurrencyFormatter = (price, currency) => {
    if (!currency) return price;
    const formatter = new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency,
    });

    return formatter.format(price);
  };

  const applyPromo = useCallback(async () => {
    let { errors, data } = await getBillingCouponInfoByPromoCode(
      promoCode,
      "event-cart"
    );

    if (errors) return alert(errors[0].message);

    const {
      getBillingCouponInfoByPromoCode: { value, unit, id, stripeCouponId, contentIds, appliesToAll },
    } = data;
    if (id) {
      setPromoCodeId(id);
      setIsPromoCode(true);
      setPromoDetails({ value, unit, contentIds, appliesToAll });

      // compute eligible subtotal for this promo
      const eligibleSubtotal = (() => {
        if (appliesToAll) return totalPrice;
        const ids = contentIds || [];
        if (!Array.isArray(ids) || ids.length === 0) return totalPrice;
        return cartItems.reduce((s, it) => {
          const itemSubtotal = it.quantity * it.pricing.price;
          const candidates = [
            it.item_detail?.id,
            it.item_detail?.databaseId,
            it.contentId,
            it.content?.id,
            it.productId,
            it.product?.id,
          ];
          const match = candidates.some((c) => c && ids.indexOf(String(c)) !== -1);
          return s + (match ? itemSubtotal : 0);
        }, 0);
      })();

      const calculatedAmount = unit === "PERCENTAGE" ? eligibleSubtotal * (value / 100) : value;
      setPromoCodeAmount(Math.min(calculatedAmount, eligibleSubtotal || totalPrice));
      getTax();
    } else {
      alert("No promo code found");
    }
  }, [getTax, promoCode, totalPrice]);

  const cancelPromo = useCallback(() => {
    setPromoCodeId("");
    setIsPromoCode(false);
    setPromoCodeAmount(0);
    setPromoCode("");
    setPromoDetails(null);
    getTax();
  }, [getTax]);

  const getCardBrandImage = (brand) => {
    brand = brand.toLowerCase();

    let cardImage;

    switch (brand) {
      case "visa":
        cardImage = VisaCard;
        break;
      default:
        break;
    }

    if (cardImage) {
      return <img alt={brand} width="30px" src={cardImage} />;
    }
  };

  const renderCardSummary = (card) => {
    if (!card) return null;
    return (
      <div
        className="card-summary d-flex align-items-center"
        style={{ gap: "8px", justifyContent: "center" }}
      >
        {getCardBrandImage(card?.brand)}
        <span>**** **** **** {card?.last4}</span>
        {card?.exp_month && card?.exp_year && (
          <span className="text-muted">
            Exp {String(card.exp_month).padStart(2, "0")}/{card.exp_year}
          </span>
        )}
      </div>
    );
  };

  const doCancel = useCallback(() => {
    // const parsedQuery = queryString.parse(location.search)
    // window.location.replace(parsedQuery['callback'] || 'https://synkd.life')

    let url =
      window.location != window.parent.location
        ? document.referrer
        : document.location.href;
    /* window.parent.postMessage("close-iframe", 'https://localhost:8080/admin/company/adminpackageoptions'); */
    window.parent.postMessage("close-iframe", "*");
  }, [location.search]);

  useEffect(() => {
    getEventProps();
    console.log(match);
  }, [getEventProps]);

  useEffect(() => {
    const parsed = queryString.parse(location.search);
    const promoFromQuery =
      typeof parsed?.promoCode === "string" ? parsed.promoCode.trim() : "";
    const currentCode = typeof promoCode === "string" ? promoCode.trim() : "";
    if (
      promoFromQuery &&
      promoFromQuery.length > 0 &&
      promoFromQuery.toLowerCase() !== currentCode.toLowerCase()
    ) {
      setPendingPromo({ code: promoFromQuery });
    }
  }, [location.search, promoCode]);

  useEffect(() => {
    getService();
  }, [getService]);

  useEffect(() => {
    getCards();
  }, [getCards]);

  useEffect(() => {
    const fetchBilling = async () => {
      try {
        if (!event?.organiser?.company?.id) return;
        const { data } = await getCompanyBillingInfo(event.organiser.company.id);
        if (data && data.getCompanyBillingInfo) {
          setBillingEmail(data.getCompanyBillingInfo.billingEmail);
          setBillingPhone(data.getCompanyBillingInfo.billingPhone);
        }
      } catch (err) {
        console.log('Error fetching company billing info', err);
      }
    };

    fetchBilling();
  }, [event]);

  useEffect(() => {
    if (
      !selectedCard &&
      Array.isArray(companyCards) &&
      companyCards.length > 0
    ) {
      setSelectedCard(companyCards[0]);
    }
  }, [companyCards, selectedCard]);

  useEffect(() => {
    if (
      !pendingCardId ||
      !Array.isArray(companyCards) ||
      companyCards.length === 0
    ) {
      return;
    }
    const match = companyCards.find((card) => card?.stripeId === pendingCardId);
    if (match) {
      setSelectedCard(match);
      setPendingCardId(null);
    }
  }, [companyCards, pendingCardId]);

  useEffect(() => {
    getTax();
  }, [getTax]);

  useEffect(() => {
    if (!pendingPromo) return;
    const incomingCode =
      typeof pendingPromo?.code === "string" ? pendingPromo.code.trim() : "";
    if (!incomingCode) {
      setPendingPromo(null);
      return;
    }

    if (
      isPromoCode &&
      promoCode &&
      promoCode.toLowerCase() !== incomingCode.toLowerCase()
    ) {
      cancelPromo();
    }

    setPromoCode(incomingCode);
    setPendingPromo(null);
    setAutoApplyPromo(incomingCode);
  }, [cancelPromo, isPromoCode, pendingPromo, promoCode]);

  useEffect(() => {
    if (!autoApplyPromo) return;
    if (
      !promoCode ||
      promoCode.toLowerCase() !== autoApplyPromo.toLowerCase()
    ) {
      return;
    }
    applyPromo();
    setAutoApplyPromo(null);
  }, [applyPromo, autoApplyPromo, promoCode]);

  useEffect(() => {
    if (!promoDetails) return;
    const value = Number(promoDetails?.value || 0);
    const unit = promoDetails?.unit;

    // determine eligible subtotal for this promo (respect contentIds/appliesToAll)
    const eligibleSubtotal = (() => {
      if (promoDetails.appliesToAll) return totalPrice;
      const ids = promoDetails.contentIds || [];
      if (!Array.isArray(ids) || ids.length === 0) return totalPrice;
      return cartItems.reduce((s, it) => {
        const itemSubtotal = it.quantity * it.pricing.price;
        const candidates = [
          it.item_detail?.id,
          it.item_detail?.databaseId,
          it.contentId,
          it.content?.id,
          it.productId,
          it.product?.id,
        ];
        const match = candidates.some((c) => c && ids.indexOf(String(c)) !== -1);
        return s + (match ? itemSubtotal : 0);
      }, 0);
    })();

    const calculated = unit === "PERCENTAGE" ? eligibleSubtotal * (value / 100) : value;
    const clamped = Math.min(calculated, eligibleSubtotal || totalPrice);
    setPromoCodeAmount((prev) =>
      Number.isFinite(clamped) && prev !== clamped ? clamped : prev
    );
  }, [promoDetails, totalPrice, cartItems]);

  useEffect(() => {
    const handlePrefillMessage = (event) => {
      if (allowedParentOrigin && event.origin !== allowedParentOrigin) {
        return;
      }
      const data = event.data;
      if (
        !data ||
        typeof data !== "object" ||
        data.type !== "PREFILL_EVENT_CART"
      ) {
        return;
      }
      if (typeof data.cardId === "string" && data.cardId) {
        setPendingCardId(data.cardId);
      }
      if (
        typeof data.promoCode === "string" &&
        data.promoCode.trim().length > 0
      ) {
        setPendingPromo({ code: data.promoCode.trim() });
      }
    };
    window.addEventListener("message", handlePrefillMessage);
    return () => window.removeEventListener("message", handlePrefillMessage);
  }, [allowedParentOrigin]);

  useEffect(() => {
    if (confirmStep === 1) {
      checkoutItem();
    }
  }, [confirmStep, checkoutItem]);

  const appliedDiscount = Math.min(promoCodeAmount, totalPrice);
  const discountedSubtotal = Math.max(totalPrice - appliedDiscount, 0);
  const grandTotal = discountedSubtotal + totalTax;

  const taxNoDiscount = useMemo(() => {
    if (!Array.isArray(cartItems) || cartItems.length === 0) return 0;
    return cartItems.reduce((acc, curr) => {
      const itemSubtotal = curr.quantity * curr.pricing.price;
      const taxPercent = isNaN(curr.pricing.tax) ? 0 : curr.pricing.tax;
      return acc + (itemSubtotal * taxPercent) / 100;
    }, 0);
  }, [cartItems]);

  const taxSaved = Math.max((taxNoDiscount || 0) - (totalTax || 0), 0);
  const discountWithTax = appliedDiscount + taxSaved;

  if (confirmStep === 2) {
    return (
      <Redirect
        to={{
          pathname: `/complete-transaction`,
          search: location.search ? location.search : null,
          state: {
            companyId: event?.organiser?.company?.id,
            card: selectedCard,
            intentClientSecret,
            serviceName: pricingInfo?.serviceName,
          },
        }}
      />
    );
  }

  return (
    <div className="main-container">
      <div className="content-container animate__animated animate__fadeIn">
        <div className="action-btns">
          <CheckSvg
            width="30px"
            height="30px"
            className="p-2 rounded"
            color="whitesmoke"
            style={{ backgroundColor: "#a489ac", cursor: "pointer" }}
            disabled={!selectedCard}
            onClick={() => setConfirmStep(1)}
          />
          <CrossSvg
            width="30px"
            height="30px"
            className="p-2 rounded"
            color="whitesmoke"
            onClick={() => doCancel()}
            style={{
              backgroundColor: "#ed1c24",
              cursor: "pointer",
              marginLeft: "8px",
            }}
          />
        </div>
        <h5 className="modal-title" style={{ marginTop: "10px" }}>
          Confirm Purchase
        </h5>

        <div>
          {cartItems.map((item) => {
            const taxPercent = isNaN(item.pricing?.tax) ? 0 : item.pricing?.tax;
            const unitInclusive = item.pricing?.price * (1 + taxPercent / 100);
            const lineTotalInclusive = item.quantity * unitInclusive;
            return (
              <div
                key={item.id || item.item_detail?.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  margin: "10px 0",
                  padding: "0 10px",
                }}
              >
                <div>
                  {item.quantity} {item.item_detail.name}
                </div>
                <div>
                  {renderCurrencyFormatter(lineTotalInclusive, currency)}
                </div>
              </div>
            );
          })}
        </div>

   

        {/* <div className="price-package">
          <p>Origional Total (+ Tax)</p>
          <p>
            <strong>
              {renderCurrencyFormatter(totalPrice + taxNoDiscount, currency)}
            </strong>
          </p>
        </div> */}

        {isPromoCode && (
          <div style={{ marginTop: "10px" }}>
            <p className="text-success mb-0">
              Coupon {" "}
              <span style={{ color: "#7b1fa2", fontWeight: 600 }}>
                {promoCode || promoDetails?.stripeCouponId || ""}
              </span>{" "}
              Applied
            </p>
          </div>
        )}

        {isPromoCode && (
          <div className="price-package">
            <p>Discount</p>
            <p>
                <strong>{renderCurrencyFormatter(discountWithTax, currency)}</strong>
            </p>
          </div>
        )}

        <div className="price-package">
          <p>Total</p>
          <p>
            <strong>{renderCurrencyFormatter(discountedSubtotal, currency)}</strong>
          </p>
          <p>Sales Tax</p>
          <p>
            <strong>{renderCurrencyFormatter(totalTax, currency)}</strong>
          </p>
          <p className="grand">Grand Total</p>
          <p className="grand">
            <strong>{renderCurrencyFormatter(grandTotal, currency)}</strong>
          </p>
        </div>

            <div className="purchase-method card-method animate__animated animate__fadeIn">
          {selectedCard ? (
            renderCardSummary(selectedCard)
          ) : (
            <p className="text-muted mb-0">No saved card available.</p>
          )}
        </div>
        <div style={{ marginTop: "10px" }}>
          <p>Billing Email: {billingEmail}</p>
          <p>Billing Phone: {billingPhone}</p>
        </div>
      </div>
    </div>
  );
};

export default withRouter(EventCart);
